/**
 * Robot Service for V2 Services
 */

import { FirestoreService } from '../db/FirestoreService';
import { Robot, RobotData, RobotStatus, RobotLocation } from '../models/Robot.model';

export class RobotService {
  private firestore: FirestoreService;
  private readonly COLLECTION = 'robots';

  constructor() {
    this.firestore = FirestoreService.getInstance();
  }

  async createRobot(robotData: Partial<RobotData>): Promise<Robot> {
    const robot = new Robot(robotData);
    const docRef = await this.firestore.addDocument(this.COLLECTION, robot.toFirestore());
    robot.id = docRef.id;
    return robot;
  }

  async getRobot(id: string): Promise<Robot | null> {
    const doc = await this.firestore.getDocument(this.COLLECTION, id);
    return doc ? Robot.fromFirestore({ id, ...doc }) : null;
  }

  async updateRobot(id: string, updates: Partial<RobotData>): Promise<void> {
    await this.firestore.updateDocument(this.COLLECTION, id, {
      ...updates,
      updatedAt: new Date()
    });
  }

  async deleteRobot(id: string): Promise<void> {
    await this.firestore.deleteDocument(this.COLLECTION, id);
  }

  async getAllRobots(): Promise<Robot[]> {
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [],
      { field: 'name', direction: 'asc' }
    );
    return docs.map(doc => Robot.fromFirestore(doc));
  }

  async getRobotsByStatus(status: RobotStatus): Promise<Robot[]> {
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [{ field: 'status', operator: '==', value: status }]
    );
    return docs.map(doc => Robot.fromFirestore(doc));
  }

  async getAvailableRobots(): Promise<Robot[]> {
    return this.getRobotsByStatus('available');
  }

  async getRobotsByBuilding(buildingId: string): Promise<Robot[]> {
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [{ field: 'location.buildingId', operator: '==', value: buildingId }]
    );
    return docs.map(doc => Robot.fromFirestore(doc));
  }

  async getRobotsWithLowBattery(threshold: number = 20): Promise<Robot[]> {
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [{ field: 'batteryLevel', operator: '<=', value: threshold }]
    );
    return docs.map(doc => Robot.fromFirestore(doc));
  }

  async getRobotsDueForMaintenance(): Promise<Robot[]> {
    const now = new Date();
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [{ field: 'nextMaintenance', operator: '<=', value: now }]
    );
    return docs.map(doc => Robot.fromFirestore(doc));
  }

  async updateRobotStatus(id: string, status: RobotStatus): Promise<void> {
    await this.updateRobot(id, { status });
  }

  async updateRobotLocation(id: string, location: RobotLocation): Promise<void> {
    const robot = await this.getRobot(id);
    if (!robot) throw new Error('Robot not found');

    robot.updateLocation(location);
    await this.updateRobot(id, robot.toFirestore());
  }

  async updateBatteryLevel(id: string, batteryLevel: number): Promise<void> {
    await this.updateRobot(id, { batteryLevel });

    if (batteryLevel <= 20) {
      await this.updateRobotStatus(id, 'charging');
    } else if (batteryLevel >= 90) {
      const robot = await this.getRobot(id);
      if (robot?.status === 'charging' && !robot.currentBookingId) {
        await this.updateRobotStatus(id, 'available');
      }
    }
  }

  async assignRobotToBooking(robotId: string, bookingId: string): Promise<void> {
    const robot = await this.getRobot(robotId);
    if (!robot) throw new Error('Robot not found');

    if (robot.status !== 'available') {
      throw new Error(`Robot is not available (current status: ${robot.status})`);
    }

    robot.assignToBooking(bookingId);
    await this.updateRobot(robotId, robot.toFirestore());
  }

  async completeRobotBooking(robotId: string, cleaningTimeHours: number = 0): Promise<void> {
    const robot = await this.getRobot(robotId);
    if (!robot) throw new Error('Robot not found');

    robot.completeBooking();
    if (cleaningTimeHours > 0) {
      robot.totalHours += cleaningTimeHours;
    }

    await this.updateRobot(robotId, robot.toFirestore());
  }

  async scheduleRobotMaintenance(id: string, maintenanceDate: Date): Promise<void> {
    await this.updateRobot(id, {
      nextMaintenance: maintenanceDate,
      status: 'maintenance'
    });
  }

  async completeRobotMaintenance(id: string, nextMaintenanceDate?: Date): Promise<void> {
    const updates: Partial<RobotData> = {
      lastMaintenance: new Date(),
      status: 'available'
    };

    if (nextMaintenanceDate) {
      updates.nextMaintenance = nextMaintenanceDate;
    } else {
      const nextDate = new Date();
      nextDate.setDate(nextDate.getDate() + 30);
      updates.nextMaintenance = nextDate;
    }

    await this.updateRobot(id, updates);
  }

  async findBestRobotForBooking(buildingId: string, floor?: number): Promise<Robot | null> {
    let robots = await this.getAvailableRobots();

    robots = robots.filter(robot =>
      robot.location?.buildingId === buildingId && robot.batteryLevel > 30
    );

    if (floor !== undefined) {
      robots = robots.filter(robot => robot.location?.floor === floor);
    }

    if (robots.length === 0) return null;

    robots.sort((a, b) => {
      if (floor !== undefined) {
        const aFloorMatch = a.location?.floor === floor ? 1 : 0;
        const bFloorMatch = b.location?.floor === floor ? 1 : 0;
        if (aFloorMatch !== bFloorMatch) return bFloorMatch - aFloorMatch;
      }

      return b.batteryLevel - a.batteryLevel;
    });

    return robots[0];
  }

  async getRobotStats(): Promise<{
    total: number;
    available: number;
    cleaning: number;
    charging: number;
    maintenance: number;
    offline: number;
    averageBattery: number;
    totalCleanings: number;
    totalHours: number;
  }> {
    const robots = await this.getAllRobots();

    const stats = robots.reduce((acc, robot) => {
      acc.total++;
      acc[robot.status]++;
      acc.averageBattery += robot.batteryLevel;
      acc.totalCleanings += robot.totalCleanings;
      acc.totalHours += robot.totalHours;
      return acc;
    }, {
      total: 0,
      available: 0,
      cleaning: 0,
      charging: 0,
      maintenance: 0,
      offline: 0,
      averageBattery: 0,
      totalCleanings: 0,
      totalHours: 0
    });

    if (stats.total > 0) {
      stats.averageBattery = Math.round(stats.averageBattery / stats.total);
    }

    return stats;
  }

  subscribeToRobots(callback: (robots: Robot[]) => void): () => void {
    return this.firestore.subscribeToCollection(
      this.COLLECTION,
      (docs) => {
        const robots = docs.map(doc => Robot.fromFirestore(doc));
        callback(robots);
      },
      [],
      { field: 'name', direction: 'asc' }
    );
  }

  subscribeToRobot(id: string, callback: (robot: Robot | null) => void): () => void {
    return this.firestore.subscribeToDocument(
      this.COLLECTION,
      id,
      (doc) => {
        const robot = doc ? Robot.fromFirestore({ id, ...doc }) : null;
        callback(robot);
      }
    );
  }

  subscribeToAvailableRobots(callback: (robots: Robot[]) => void): () => void {
    return this.firestore.subscribeToCollection(
      this.COLLECTION,
      (docs) => {
        const robots = docs.map(doc => Robot.fromFirestore(doc));
        callback(robots);
      },
      [{ field: 'status', operator: '==', value: 'available' }]
    );
  }
}